'use strict';

/**
 * Mobile Navigation × Modals integration.
 *
 * Implements the close-then-reopen flow when a user clicks a modal trigger
 * from within the open Mobile Navigation dialog.
 */

/**
 * @type {string}
 * @private
 */
const _RETURN_FROM_MODAL_EVENT = 'mobileNavigation:returnFromModal';

/**
 * @type {string[]}
 * @private
 */
const _MODAL_DATA_ATTRIBUTES = [
    'data-modal-trigger',
    'data-subscription',
    'data-cookie-settings',
    'data-ccpa-action',
    'data-voc-trigger',
    'data-quickview-id',
    'data-quickview-set',
    'data-quickview-location',
    'data-video-banner',
    'data-video-banner-modal-trigger',
    'data-gc-trigger',
    'data-attentive-modal',
    'data-attentive-creative',
    'data-ambassador-modal',
    'data-auth-modal-trigger',
    'data-original-href',
];

/**
 * Attaches a capture-phase click listener to a single modal link.
 *
 * @param {HTMLElement} el - Modal link inside the mobile navigation dialog.
 * @param {HTMLDialogElement} dialog - Mobile navigation dialog element.
 * @param {object} nav - Mobile navigation API exposed on `window.MobileNavigation`.
 * @param {{ current: string[]|null }} savedPathRef - Mutable saved drilldown path.
 * @private
 */
function _attachModalLinkHandler(el, dialog, nav, savedPathRef) {
    if (el._mobileNavHandlerAttached) return;
    el._mobileNavHandlerAttached = true;

    /** @type {Record<string, string>} */
    const savedAttrs = {};
    _MODAL_DATA_ATTRIBUTES.forEach((attr) => {
        if (el.hasAttribute(attr)) {
            savedAttrs[attr] = el.getAttribute(attr);
        }
    });

    el.addEventListener(
        'click',
        (event) => {
            const modals = window.CnvcamModals;

            if (!modals) {
                console.warn(
                    'Mobile Navigation modals integration: window.CnvcamModals is not available. Modal will not open.',
                );
                return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();

            savedPathRef.current = nav.getVisiblePath();
            nav.close();

            const onAnimationEnd = (ev) => {
                if (ev.target !== dialog) return;
                dialog.removeEventListener('animationend', onAnimationEnd);

                if (savedAttrs['data-subscription']) {
                    modals.openSubscriptionModal(
                        savedAttrs['data-subscription'],
                        modals.dispatchReturnFromModal,
                    );
                    return;
                }

                Object.entries(savedAttrs).forEach(([key, value]) => {
                    if (!el.hasAttribute(key)) {
                        el.setAttribute(key, value);
                    }
                });

                modals.openModalFromElement(el, modals.dispatchReturnFromModal);
            };

            dialog.addEventListener('animationend', onAnimationEnd);
        },
        true,
    );
}

/**
 * Initializes mobile navigation modal close-then-reopen integration.
 */
export function initMobileNavigationModalsIntegration() {
    const dialog = document.getElementById('mobile-navigation-dialog');
    const nav = window.MobileNavigation;

    if (!dialog || !nav) return;

    /**
     * @type {{ current: string[]|null }}
     * @private
     */
    const savedPathRef = { current: null };

    document.addEventListener(_RETURN_FROM_MODAL_EVENT, () => {
        nav.open();
        if (savedPathRef.current) {
            nav.setVisiblePath(savedPathRef.current);
            savedPathRef.current = null;
        }
    });

    const links = dialog.querySelectorAll('[data-mobile-modal]');
    links.forEach((el) => {
        _attachModalLinkHandler(el, dialog, nav, savedPathRef);
    });
}
